import React, { useState, useEffect } from "react";
import { parseMarkdown, downloadMarkdown, downloadHtml, downloadChatTxt, downloadChatJson, downloadChatHtml } from "../utils/utils";
import {
  ClipboardList,
  X,
  Save,
  Mail,
  Copy,
  FileCode,
  FileText,
  FileJson,
  MessageSquare,
  Check,
} from "lucide-react";
import { LoadingButton } from "../../../components/LoadingButton";
import { ErrorAlert } from "../../../components/ErrorAlert";
import { useSummaryGenerator } from "../hooks/useSummaryGenerator";
import { useSummaryEmail } from "../hooks/useSummaryEmail";

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingTranscript: { timestamp: string; orig: string; tran: string; isMic?: boolean }[];
  currentMeetingId: string | null;
  summaryServiceUrl: string;
  targetLang: string;
  targetLangLabel: string;
  initialSummaryText?: string;
  isSavedSummary?: boolean;
}

export function SummaryModal({
  isOpen,
  onClose,
  meetingTranscript,
  currentMeetingId,
  summaryServiceUrl,
  targetLang,
  targetLangLabel,
  initialSummaryText = "",
  isSavedSummary = false,
}: SummaryModalProps) {
  const {
    summaryText,
    htmlFilename,
    loading,
    error,
    isPolling,
    generateSummary,
    resetGeneration,
    setSavedSummary,
    setError,
  } = useSummaryGenerator({
    summaryServiceUrl,
    currentMeetingId,
    targetLang,
    targetLangLabel,
    meetingTranscript,
  });

  const {
    emailInput,
    setEmailInput,
    emailLoading,
    emailStatus,
    emailMessage,
    handleSendEmail,
    resetEmailState,
  } = useSummaryEmail(summaryServiceUrl, currentMeetingId);

  const [copied, setCopied] = useState(false);
  const [showMinutesMenu, setShowMinutesMenu] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isSavedSummary) {
        setSavedSummary(initialSummaryText);
      } else if (meetingTranscript.length > 0 && !summaryText && !loading) {
        generateSummary();
      }
    }
  }, [isOpen, isSavedSummary, initialSummaryText]);

  useEffect(() => {
    if (!isOpen) {
      resetGeneration();
      resetEmailState();
    }
  }, [isOpen, resetGeneration, resetEmailState]);

  const handleCopy = async () => {
    if (!summaryText) return;
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = summaryText;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        alert("No se pudo copiar automáticamente.");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-3xl w-full max-h-[85vh] shadow-2xl flex flex-col overflow-hidden animate-[modal-appear_0.22s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="summary-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-[var(--text-dim)]" />
            <h2 id="summary-modal-title" className="text-sm font-semibold text-[var(--text)] uppercase tracking-wider font-mono">
              {isSavedSummary ? "Minuta Guardada (Historial)" : "Minuta & Resumen de Reunión (IA)"}
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
            aria-label="Cerrar modal de minuta"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
              <div className="w-10 h-10 border-4 border-[var(--accent)]/20 border-t-[var(--accent)] rounded-full animate-spin"></div>
              <p className="font-mono text-xs text-[var(--text-dim)] animate-pulse">
                {isPolling
                  ? "La IA está procesando la minuta..."
                  : "Generando minuta y estructurando acuerdos con IA..."}
              </p>
              {isPolling && (
                <p className="text-[10px] text-[var(--text-dim)] max-w-xs leading-relaxed">
                  La generación tomó más de 29s. La Lambda sigue corriendo en segundo plano — te avisamos cuando termine.
                </p>
              )}
            </div>
          )}

          {error && (
            <ErrorAlert error={error} onDismiss={() => setError("")}>
              <button 
                onClick={generateSummary}
                className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded text-xs text-red-300 font-medium transition-all"
              >
                Reintentar
              </button>
            </ErrorAlert>
          )}

          {!loading && !error && summaryText && (
            <div className="flex flex-col gap-4">
              {htmlFilename && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-[11px] font-mono">
                  <Save size={13} />
                  <span>
                    Guardada en servidor: <strong>uploads/meetings/{htmlFilename}</strong>
                  </span>
                </div>
              )}

              <div 
                className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-5 text-sm overflow-y-auto max-h-[45vh] leading-relaxed select-text"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(summaryText) }}
              />

              {emailMessage && (
                <div 
                  className={`px-4 py-2.5 rounded-lg text-xs leading-normal font-medium ${
                    emailStatus === "success" 
                      ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-450" 
                      : "bg-red-500/10 border border-red-500/20 text-red-450"
                  }`}
                >
                  {emailMessage}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {!loading && (summaryText || error) && (
          <div className="px-5 py-4 bg-[var(--surface)] border-t border-[var(--border)] flex flex-col md:flex-row md:items-center justify-between gap-4 flex-shrink-0">
            {summaryText && (
              <form onSubmit={handleSendEmail} className="flex items-center gap-1.5 p-1 bg-[var(--surface)] border border-[var(--border)] rounded-lg w-full md:max-w-xs">
                <input
                  type="text"
                  placeholder="Enviar a otro correo..."
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="bg-transparent border-none py-1.5 px-2.5 text-xs flex-1 text-[var(--text)] placeholder-[var(--text-dim)] min-w-0"
                  disabled={emailLoading}
                />
                <LoadingButton
                  type="submit"
                  isLoading={emailLoading}
                  className="px-3 py-1.5 bg-accent hover:bg-accent/80 disabled:bg-surface disabled:opacity-50 text-white font-semibold text-xs rounded transition-all whitespace-nowrap min-w-[70px] cursor-pointer"
                >
                  {emailLoading ? (
                    "Enviando..."
                  ) : (
                    <>
                      <Mail size={12} />
                      <span>Enviar</span>
                    </>
                  )}
                </LoadingButton>
              </form>
            )}

            <div className="flex items-center gap-2.5 justify-end ml-auto">
              {summaryText && (
                <>
                  <button
                    onClick={handleCopy}
                    className="px-3 py-1.5 border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-hover)] text-[var(--text-muted)] rounded text-xs transition-all active:scale-95 font-medium cursor-pointer flex items-center gap-1.5"
                  >
                    {copied ? (
                      <>
                        <Check size={12} className="text-emerald-500" />
                        <span>Copiado</span>
                      </>
                    ) : (
                      <>
                        <Copy size={12} />
                        <span>Copiar texto</span>
                      </>
                    )}
                  </button>

                  <div className="relative">
                    <button
                      onClick={() => setShowMinutesMenu(!showMinutesMenu)}
                      className="px-3 py-1.5 border border-[var(--accent)]/25 bg-[var(--accent-muted)] hover:bg-[var(--accent-muted)]/1.5 text-[var(--accent)] rounded text-xs transition-all active:scale-95 font-semibold cursor-pointer flex items-center gap-1.5"
                    >
                      <Save size={12} />
                      <span>Download Minutes</span>
                    </button>
                    
                    {showMinutesMenu && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowMinutesMenu(false)} />
                        <div className="absolute bottom-full right-0 mb-2 w-36 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-xl p-1 z-50 flex flex-col gap-1">
                          <button
                            onClick={() => { downloadMarkdown(summaryText); setShowMinutesMenu(false); }}
                            className="flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--text)] hover:bg-[var(--surface-hover)] rounded-md transition-colors"
                          >
                            <FileCode size={12} className="text-purple-400" />
                            Markdown
                          </button>
                          <button
                            onClick={() => { downloadHtml(summaryText); setShowMinutesMenu(false); }}
                            className="flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--text)] hover:bg-[var(--surface-hover)] rounded-md transition-colors"
                          >
                            <FileCode size={12} className="text-blue-400" />
                            HTML Page
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              {meetingTranscript.length > 0 && !isSavedSummary && (
                <div className="relative">
                  <button
                    onClick={() => setShowChatMenu(!showChatMenu)}
                    className="px-3 py-1.5 border border-amber-500/25 bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 rounded text-xs transition-all active:scale-95 font-semibold cursor-pointer flex items-center gap-1.5"
                  >
                    <MessageSquare size={12} />
                    <span>Download Chat</span>
                  </button>
                  
                  {showChatMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowChatMenu(false)} />
                      <div className="absolute bottom-full right-0 mb-2 w-32 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-xl p-1 z-50 flex flex-col gap-1">
                        <button
                          onClick={() => { downloadChatTxt(meetingTranscript); setShowChatMenu(false); }}
                          className="flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--text)] hover:bg-[var(--surface-hover)] rounded-md transition-colors"
                        >
                          <FileText size={12} className="text-gray-400" />
                          TXT
                        </button>
                        <button
                          onClick={() => { downloadChatJson(meetingTranscript); setShowChatMenu(false); }}
                          className="flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--text)] hover:bg-[var(--surface-hover)] rounded-md transition-colors"
                        >
                          <FileJson size={12} className="text-orange-400" />
                          JSON
                        </button>
                        <button
                          onClick={() => { downloadChatHtml(meetingTranscript); setShowChatMenu(false); }}
                          className="flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--text)] hover:bg-[var(--surface-hover)] rounded-md transition-colors"
                        >
                          <FileCode size={12} className="text-blue-400" />
                          HTML
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-accent hover:bg-accent/80 text-white rounded text-xs transition-all active:scale-95 font-semibold cursor-pointer"
              >
                Listo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
